import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import Loader from '../loader';
import ImageLibraryView from './ImageLibraryView';
import db from '../../utils/dexie/driverappdb';
import queries from '../../utils/dexie/queries';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

const ImageLibraryContainer = forwardRef(({ dropId, onlineImagesEnabled = false, isEditable = false, internalImages = false, onDeleteCallback = () => {} }, ref) => {

    const [t] = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const [loadingVariant, setLoadingVariant] = useState();
    const [loadingText, setLoadingText] = useState("");
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [images, setImages] = useState([]);
    const [appParams, setAppParams] = useState([]);
    const [drop, setDrop] = useState();
    const { enqueueSnackbar } = useSnackbar();
    let history = useHistory();

    useEffect(() => {
        
        db.transaction('r', db.drops, db.images, db.params, async () => {
            setAppParams(await db.params.where('id').notEqual(0).first());
            setDrop(await db.drops.get(parseInt(dropId)));

            await db.images.where('dropId').equals(parseInt(dropId))
                .and(image => image.internal === internalImages && !image.deleted)
                .toArray()
                .then((result) => {
                    let imgs = [];
                    for (let index = 0; index < result.length; index++) {
                        const img = result[index];
                        imgs.push({
                            src: img.blob,
                            width: 3,
                            height: 3,
                            id: img.id,
                            key: img.id.toString(),
                            lastModified: img.lastModified
                        });
                    }
                    return imgs;
                })
                .then(imgs => setImages(imgs));
        })
        .finally(() => setIsLoading(false));
        
    }, [dropId, internalImages]);

    const onImageDelete = (imageId) => {
        try {

            if (imageId != null) {
                db.transaction('rw', db.images, db.actions, async () => {
                    const parsedImageId = parseInt(imageId);
                    const imageToDelete = await db.images.get(parsedImageId);
                    if (imageToDelete != null) {
                        let now = new Date();
                        let utc = now.getTime() - now.getTimezoneOffset() * 60000;
                        await db.actions.put({ type: "ImageDelete", key: parsedImageId, time: utc });
                        await db.images.update(parsedImageId, { deleted: true });
                    }

                    let array = [...images];
                    let item = array.find(x => x.id === imageId);
                    let index = array.indexOf(item)
                    if (index !== -1) {
                        array.splice(index, 1);
                        setImages(array);
                    }
                })
                .then(onDeleteCallback);
            }

        } catch (error) {
            queries.addErrorToLogs(error);
            history.push(`/error`);
        }
    }

    const readAndCompressFile = (file, maxSizeMB, onProgress) => {
        return new Promise((resolve, reject) => {
            onProgress(10);
            const reader = new FileReader();
            reader.onload = (e) => {
                onProgress(40);

                const img = new Image();
                img.onload = () => {
                    try {
                        onProgress(60);

                        const canvas = document.createElement('canvas');
                        let { width, height } = img;

                        // Schaal indien nodig
                        const MAX_DIMENSION = 1920;
                        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                            const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
                            width = Math.round(width * ratio);
                            height = Math.round(height * ratio);
                        }

                        canvas.width = width;
                        canvas.height = height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(img, 0, 0, width, height);

                        onProgress(80);

                        // Comprimeer met afnemende kwaliteit tot onder maxSizeMB
                        const maxBytes = maxSizeMB * 1024 * 1024;
                        let quality = 0.9;
                        let dataUrl = canvas.toDataURL('image/jpeg', quality);

                        // base64 is ~33% groter dan binair, corrigeer daarvoor
                        while ((dataUrl.length * 0.75) > maxBytes && quality > 0.1) {
                            quality = Math.round((quality - 0.1) * 10) / 10;
                            dataUrl = canvas.toDataURL('image/jpeg', quality);
                        }

                        onProgress(100);
                        resolve({ dataUrl, name: file.name, lastModified: file.lastModified });
                    } catch (err) {
                        reject(new Error(`Compression failed for ${file.name}: ${err.message}`));
                    }
                };
                img.onerror = () => reject(new Error(`Image load failed: ${file.name}`));
                img.src = e.target.result;
            };
            reader.onerror = () => reject(new Error(reader.error?.message ?? `File could not be read: ${file.name}`));
            reader.readAsDataURL(file);
        });
    };

    useImperativeHandle(ref, () => ({

        addImages: async (files) => {
            try {
                setLoadingVariant("determinate");
                setIsLoading(true);

                let filesToHandle = [...files];
                let supportedFileTypes = ["image/jpeg", "image/png", "image/bmp"];
                if (filesToHandle)
                    filesToHandle = filesToHandle.filter(file => supportedFileTypes.indexOf(file.type) > -1);

                if (files.length > filesToHandle.length) {
                    enqueueSnackbar(t('UnsupportedFileFormatSelection'), {
                        variant: 'error',
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'right'
                        },
                        autoHideDuration: 5000
                    });
                }

                if (filesToHandle && filesToHandle.length > 0) {
                    for (let fileIndex = 0; fileIndex < filesToHandle.length; fileIndex++) {
                        setLoadingText(`${t("Image")} ${fileIndex + 1} ${t("Of")} ${filesToHandle.length}`);
                        setLoadingProgress(0);

                        const { dataUrl, name, lastModified } = await readAndCompressFile(
                            filesToHandle[fileIndex],
                            appParams.imagesMaxSizeMb,
                            setLoadingProgress
                        );

                        await db.transaction('rw', db.images, db.actions, async () => {
                            return db.images.put({
                                dropId: parseInt(dropId), 
                                blob: dataUrl, 
                                name: name, 
                                documentId: 0,
                                deleted: false,
                                lastModified: new Date(lastModified),
                                internal: internalImages
                            })
                            .then(async (id) => {
                                setImages(images => [...images, {
                                    src: dataUrl,
                                    width: 3,
                                    height: 3,
                                    id: id,
                                    key: id.toString(),
                                    lastModified: new Date(lastModified)
                                }]);
            
                                let now = new Date();
                                var utc = now.getTime() - now.getTimezoneOffset() * 60000;
                                await db.actions.put({ type: "ImageAdd", key: id, time: utc });
                                setLoadingText("");
                            }).catch(err => { throw err instanceof Error ? err : new Error(String(err)) });
                        })
                        .then(() => {
                            if (fileIndex === filesToHandle.length - 1) {
                                setIsLoading(false);
                                setLoadingVariant();
                            }
                        });
                    }
                } else {
                    setIsLoading(false);
                    setLoadingVariant();
                }

            } catch (error) {
                queries.addErrorToLogs(error);
                history.push(`/error`);
            }
        }

    }));

    return (
        <div>
            {
                isLoading ?
                    <Loader variant={loadingVariant} loadingText={loadingText} progress={loadingProgress} /> : 
                    <ImageLibraryView dropId={dropId} drop={drop} images={images} isEditable={isEditable} 
                        onlineImagesEnabled={onlineImagesEnabled} internalImages={internalImages} onDeleteCallback={onImageDelete} />
            }
        </div>
    );

})

export default ImageLibraryContainer;